'use client';
import type { GetServerSidePropsContext, PreviewData } from 'next';
import type { ParsedUrlQuery } from 'querystring';
import useSWR, { SWRResponse } from 'swr';

export type User = {
  id: string;
  name: string;
  email: string;
  userName: string;
  phone?: string;
  createDate: string;
  lastUpdated: string | null;
  initialCognitoApp: string;
  salesforce: string;
};

export type NextServerContext = GetServerSidePropsContext<
  ParsedUrlQuery,
  PreviewData
>;

const SLUG = '/api/user/self';
const baseUrl = process?.env?.NEXTAUTH_URL ?? '';

const ALLOWED_COOKIES = new Set([
  // Authentication cookies
  'next-auth.csrf-token',
  'next-auth.session-token',
]);

const fetcher = async (url: string, context?: NextServerContext) => {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      ...getHeadersFromContext(context),
    },
  });
  return response.json();
};

export const getHeadersFromContext = (
  context?: NextServerContext
): HeadersInit => {
  const cookie = Object.entries(context?.req?.cookies ?? {})
    .filter(([name]) => ALLOWED_COOKIES.has(name))
    .map(([name, value]) => `${name}=${value}`)
    .join(';');

  const headers: HeadersInit = {};
  if (cookie) {
    headers.cookie = cookie;
  }
  return headers;
};

export async function getUser(context?: NextServerContext): Promise<User> {
  const data = await fetcher(`${baseUrl}${SLUG}`, context);
  return data?.data;
}

export default function useUser(initialData?: any): SWRResponse<User> {
  const swr = useSWR(SLUG, () => getUser(), {
    fallbackData: initialData,
    revalidateOnMount: !initialData,

    // Reduce network requests
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 10000,

    // Reduce error retries
    errorRetryCount: 2,
    // https://swr.vercel.app/docs/error-handling#error-retry
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
      // Never retry on 404.
      if (error.status === 404) return;

      if (!config.errorRetryCount || retryCount >= config.errorRetryCount) {
        return;
      }

      // Retry after 5 seconds.
      setTimeout(
        () => revalidate({ retryCount: retryCount + 1 }),
        config.errorRetryInterval
      );
    },
    /** SWR Defaults
    revalidateOnFocus: true,
    revalidateOnReconnect: true,
    refreshWhenHidden: false,
    refreshWhenOffline: false,
    dedupingInterval: 2000,
    errorRetryInterval: 5000,
    */
  });

  return swr;
}
